import { css } from "@emotion/css";
import { space, text } from "@octopusdeploy/design-system-tokens";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { ArgoCDGatewayResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { generateArgoCDGatewayNamespace } from "~/areas/infrastructure/components/ArgoCDInstances/createArgoCDGatewayHelmCommand";
import CopyValueToClipboard from "~/components/CopyToClipboardButton/CopyValueToClipboard";
import DeleteDialog from "~/components/Dialog/DeleteDialog";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { generateReleaseName } from "~/utils/HelmCommands/HelmCommandHelperMethods";
type DeleteArgoCDInstanceDialogProps = {
    show: boolean;
    onClose: () => void;
    gateway: ArgoCDGatewayResource;
};
const styles = {
    container: css({
        marginTop: space[24],
    }),
    boldText: css({
        font: text.body.bold.medium,
    }),
    listStyle: css({
        listStyleType: "decimal",
        paddingLeft: space[16],
    }),
};
export const DeleteArgoCDInstanceDialog = (props: DeleteArgoCDInstanceDialogProps) => {
    const { navigate } = useSpaceAwareNavigation();
    const namespace = props.gateway.InstallDetails?.KubernetesNamespace || generateArgoCDGatewayNamespace(props.gateway.Name);
    const helmReleaseName = props.gateway.InstallDetails?.HelmReleaseName || generateReleaseName(props.gateway.Name);
    const helmUninstallCommand = `helm uninstall --namespace ${namespace} ${helmReleaseName}`;
    const deleteNamespaceCommand = `kubectl delete namespace ${namespace}`;
    const { execute: deleteGateway } = useMutation({
        name: "deleteArgoCDGateway",
        action: async (repo) => await repo.ArgoCDGateways.delete(props.gateway.Id),
        afterComplete: async () => navigate(links.argoCDInstancesPage.generateUrl({ spaceId: props.gateway.SpaceId })),
    });
    return (<DeleteDialog title={"Are you sure you want to delete this Argo CD instance"} onDeleteClick={async () => {
            await deleteGateway();
            //we don't want the delete to close the dialog as we are forcibly navigating away
            return false;
        }} open={props.show} onClose={props.onClose} renderContent={() => (<>
                    {/*{errorInfo && <ErrorPanel error={errorInfo} />}*/}
                    <p>You're about to permanently delete this Argo CD instance in Octopus. This action can't be undone.</p>

                    <div className={styles.container}>
                        <p className={styles.boldText}>Additional steps required:</p>
                        <p>To fully remove the Octopus gateway from your cluster, execute the following commands:</p>
                        <ol className={styles.listStyle}>
                            <li>
                                <CopyValueToClipboard value={helmUninstallCommand}>
                                    <code>{helmUninstallCommand}</code>
                                </CopyValueToClipboard>
                            </li>
                            <li>
                                <CopyValueToClipboard value={deleteNamespaceCommand}>
                                    <code>{deleteNamespaceCommand}</code>
                                </CopyValueToClipboard>
                            </li>
                        </ol>
                    </div>
                </>)}/>);
};
